import { ID, IDPrefixes, PrefixedID, Relationship } from '@griply/types';
import {
  buildRelationshipsMap,
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from '@griply/utils';
import { useMemo } from 'react';
import { useRelationshipsQuery } from 'shared/hooks/queries/use-relationships-query';

import { useUser } from './use-user';

export const useRelationships = () => {
  const user = useUser();
  const { data } = useRelationshipsQuery(user?.uid);

  return useMemo(() => data ?? [], [data]);
};

export const useRelationshipIdsById = (
  id: ID | undefined,
  prefixOption: IDPrefixes,
) => {
  const relationships = useRelationships();
  return useSelectedRelationShipIdsById(id, prefixOption, relationships);
};

export const useSelectedRelationShipIdsById = (
  id: ID | undefined,
  prefixOption: IDPrefixes,
  relationships: Relationship[],
) => {
  const relationshipsMap = useRelationshipsMap(relationships);

  return useMemo(() => {
    const relationBase: Record<IDPrefixes, ID[]> = {
      [IDPrefixes.Task]: [],
      [IDPrefixes.Goal]: [],
      [IDPrefixes.LifeArea]: [],
      [IDPrefixes.Tag]: [],
    };

    if (!id) {
      return relationBase;
    }

    const relatedIds = relationshipsMap[createPrefixId(id, prefixOption)];

    return (
      relatedIds?.reduce((acc, prefixedId) => {
        const idPrefix = getPrefixedIdPrefix(prefixedId);

        if (!idPrefix) {
          return acc;
        }

        acc[idPrefix].push(getPrefixedId(prefixedId));
        return acc;
      }, relationBase) ?? relationBase
    );
  }, [id, prefixOption, relationshipsMap]);
};

export const useRelationshipsMap = (relationships: Relationship[]) =>
  useMemo<Record<PrefixedID, PrefixedID[]>>(
    () => buildRelationshipsMap(relationships),
    [relationships],
  );
